import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { auth, db } from '../../firebase/config';
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit, updateDoc, arrayUnion } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { Bell, Award, FileText, Brain, User, LogOut, TrendingUp, Calendar, Eye, Trophy, X, CheckCircle } from 'lucide-react';
import EditDetailsForm from './EditDetailsForm';
import { resolveStudentRollNumber } from '../../utils/studentRoutes';
import { getStudentPhotoUrl } from '../../utils/mediaUrls';

const toDateValue = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDisplayDate = (value) => {
  const date = toDateValue(value);
  if (!date) return '';

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

function StudentDashboard() {
  const [rollNumber, setRollNumber] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { viewId } = useParams();
  const [student, setStudent] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [iaTest1, setIaTest1] = useState(null);
  const [iaTest2, setIaTest2] = useState(null);
  const [topPerformersIAT1, setTopPerformersIAT1] = useState([]);
  const [topPerformersIAT2, setTopPerformersIAT2] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const [activeLeaderboard, setActiveLeaderboard] = useState('iat1');
  const [isMentorView, setIsMentorView] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [expandedAnnouncement, setExpandedAnnouncement] = useState(null);

  const addMentorParam = (url) => {
    return isMentorView ? `${url}?mentorView=true` : url;
  };

  // ✅ Fetch mentor and coordinator names from their respective collections
  const fetchMentorAndCoordinator = async (batch, division) => {
    try {
      let mentorName = 'N/A';
      let ccName = 'N/A';

      // Fetch mentor name from batchAssignments using batch as document ID
      if (batch) {
        const batchDoc = await getDoc(doc(db, 'batchAssignments', batch));
        if (batchDoc.exists()) {
          mentorName = batchDoc.data().mentorName || 'N/A';
        }
      }

      // Fetch coordinator name from divisionAssignments using division as document ID
      if (division) {
        const divisionDoc = await getDoc(doc(db, 'divisionAssignments', division));
        if (divisionDoc.exists()) {
          const divisionData = divisionDoc.data();
          ccName = divisionData.ccName || divisionData.coordinatorName || 'N/A';
        }
      }

      return { mentorName, ccName };
    } catch (error) {
      console.error('Error fetching mentor/coordinator:', error);
      return { mentorName: 'N/A', ccName: 'N/A' };
    }
  };

  // ✅ SECURITY FIX: Check authentication and get roll number from sessionStorage
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        console.log('No user authenticated, redirecting to login');
        navigate('/student-login');
        return;
      }

      try {
        if (viewId) {
          const routeRollNumber = resolveStudentRollNumber({ viewId });
          if (routeRollNumber) {
            setRollNumber(routeRollNumber);
            setIsMentorView(new URLSearchParams(location.search).get('mentorView') === 'true');
            return;
          }
        }

        // Get student data from sessionStorage
        const studentSession = sessionStorage.getItem('studentSession');
        
        if (!studentSession) {
          console.error('Student session not found in sessionStorage');
          navigate('/student-login');
          return;
        }

        const sessionData = JSON.parse(studentSession);
        
        // Verify we have a roll number
        if (!sessionData.rollNumber) {
          console.error('Roll number not found in session data');
          navigate('/student-login');
          return;
        }

        // Set the roll number from sessionStorage
        console.log('✅ Student authenticated:', sessionData.rollNumber);
        setRollNumber(sessionData.rollNumber);
        
      } catch (error) {
        console.error('Error reading session data:', error);
        navigate('/student-login');
      }
    });

    return () => unsubscribe();
  }, [navigate, viewId, location.search]);


  // Fetch announcements for student
  const fetchAnnouncements = async () => {
    try {
      const announcementsRef = collection(db, 'announcements');
      const now = new Date();
      const snapshot = await getDocs(announcementsRef);
      
      let data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
        expiryDate: doc.data().expiryDate || new Date().toISOString()
      }));

      data = data.filter(ann => new Date(ann.expiryDate) > now);
      data.sort((a, b) => b.createdAt - a.createdAt);
      setAnnouncements(data);
    } catch (err) {
      console.error('Error fetching announcements:', err);
      setAnnouncements([]);
    }
  };

  const isRead = (announcement) => {
    if (!announcement.readBy || !Array.isArray(announcement.readBy)) return false;
    return announcement.readBy.some(reader => 
      reader.userEmail === student?.email || reader.userEmail === rollNumber
    );
  };

  const handleMarkAsRead = async (announcementId) => {
    try {
      const announcementRef = doc(db, 'announcements', announcementId);
      await updateDoc(announcementRef, {
        readBy: arrayUnion({
          userEmail: student?.email || rollNumber,
          userName: student?.name || rollNumber,
          readAt: new Date().toISOString()
        })
      });
      fetchAnnouncements();
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  useEffect(() => {
    // ✅ SECURITY FIX: Don't run if rollNumber isn't set yet
    if (!rollNumber) return;
    
    // Check for mentor access
    const params = new URLSearchParams(location.search);
    if (params.get('mentorView') === 'true') {
      const mentorAccessStr = sessionStorage.getItem('mentorAccess') || localStorage.getItem('mentorAccess');
      if (mentorAccessStr) {
        try {
          const mentorAccess = JSON.parse(mentorAccessStr);
          if (mentorAccess.studentRollNumber === rollNumber) {
            setIsMentorView(true);
            sessionStorage.setItem('mentorAccess', mentorAccessStr);
            localStorage.removeItem('mentorAccess');
          }
        } catch (error) {
          console.error('Error:', error);
        }
      }
    }
    
    loadStudentData();
    loadTopPerformers();
    fetchAnnouncements();
  }, [rollNumber, location.search]);

const openDocument = async (documentUrl) => {
    if (!documentUrl) {
      alert('Document URL not found.');
      return;
    }
    
    try {
      // Fetch file through proxy and open as blob.
      const fileUrl = /^https?:\/\//i.test(documentUrl)
        ? documentUrl
        : `/${documentUrl.replace(/^\/+/, '')}`;
      const response = await fetch(fileUrl);
      
      if (!response.ok) {
        throw new Error('Failed to load document');
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      console.log('📄 Opening document:', documentUrl);
      window.open(blobUrl, '_blank');
      
      // Clean up blob URL after a delay
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      console.error('Error opening document:', error);
      alert('Failed to open document. Please make sure both servers are running.');
    }
  };

const loadStudentData = async () => {
  try {
    // Load student profile
    const studentDoc = await getDoc(doc(db, 'students', rollNumber));
    if (studentDoc.exists()) {
      const studentData = studentDoc.data();
      
      // ✅ Fetch mentor and coordinator names based on batch and division
      const { mentorName, ccName } = await fetchMentorAndCoordinator(
        studentData.batch,
        studentData.division
      );
      
      // Update student data with fetched names
      setStudent({
        ...studentData,
        mentorName: mentorName,
        ccName: ccName
      });
    }

    // Load attendance
    const attendanceDoc = await getDoc(doc(db, 'attendance', rollNumber));
    if (attendanceDoc.exists()) {
      setAttendance(attendanceDoc.data());
    }

    // Load IA Test 1
    const iaTest1Doc = await getDoc(doc(db, 'iaTest1', rollNumber));
    if (iaTest1Doc.exists()) {
      setIaTest1(iaTest1Doc.data());
    }

    // Load IA Test 2
    const iaTest2Doc = await getDoc(doc(db, 'iaTest2', rollNumber));
    if (iaTest2Doc.exists()) {
      setIaTest2(iaTest2Doc.data());
    }

    // Load achievements - only show VERIFIED achievements
    const achievementsQuery = query(
      collection(db, 'achievements'),
      where('rollNumber', '==', rollNumber),
      where('status', '==', 'verified'),
      orderBy('submittedAt', 'desc')
    );
    const achievementsSnapshot = await getDocs(achievementsQuery);
    const achievementsData = achievementsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setAchievements(achievementsData);

    // Load notifications - FIXED PATH
    try {
      const notificationsRef = collection(db, 'notifications', rollNumber, 'messages');
      const notificationsQuery = query(
        notificationsRef,
        where('isRead', '==', false),
        orderBy('createdAt', 'desc')
      );
      const notificationsSnapshot = await getDocs(notificationsQuery);
      const notificationsData = notificationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('📬 Notifications loaded:', notificationsData);
      
      // Add automatic undertaking notification if attendance < 75%
      const attendanceData = (await getDoc(doc(db, 'attendance', rollNumber))).data();
      if (attendanceData && attendanceData.overallPercentage < 75) {
        const undertakingNotification = {
          id: 'attendance-undertaking-auto',
          icon: '⚠️',
          title: 'Attendance Undertaking Required',
          message: `Your attendance is ${attendanceData.overallPercentage.toFixed(2)}%. Please download the undertaking letter, fill it, get it signed by your parent/guardian, and submit it to your Class Coordinator within the next 3 days.`,
          fromName: 'Academic Office',
          createdAt: { toDate: () => new Date() },
          isRead: false,
          actionUrl: '/student/attendance-undertaking',
          actionText: '📄 Download Undertaking Letter'
        };
        notificationsData.unshift(undertakingNotification);
      }
      
      setNotifications(notificationsData);
    } catch (error) {
      console.log('No notifications found:', error);
      
      // Even if no notifications exist, check attendance and add undertaking notification
      try {
        const attendanceData = (await getDoc(doc(db, 'attendance', rollNumber))).data();
        if (attendanceData && attendanceData.overallPercentage < 75) {
          const undertakingNotification = {
            id: 'attendance-undertaking-auto',
            icon: '⚠️',
            title: 'Attendance Undertaking Required',
            message: `Your attendance is ${attendanceData.overallPercentage.toFixed(2)}%. Please download the undertaking letter, fill it, get it signed by your parent/guardian, and submit it to your Class Coordinator within the next 3 days.`,
            fromName: 'Academic Office',
            createdAt: { toDate: () => new Date() },
            isRead: false,
            actionUrl: '/student/attendance-undertaking',
            actionText: '📄 Download Undertaking Letter'
          };
          setNotifications([undertakingNotification]);
        } else {
          setNotifications([]);
        }
      } catch (err) {
        setNotifications([]);
      }
    }

    setLoading(false);
  } catch (error) {
    console.error('Error loading student data:', error);
    setLoading(false);
  }
};

const handleMarkNotificationAsRead = async (notificationId) => {
  try {
    const notifRef = doc(db, 'notifications', rollNumber, 'messages', notificationId);
    await updateDoc(notifRef, {
      isRead: true,
      readAt: new Date()
    });
    loadStudentData();
  } catch (error) {
    console.error('Error marking notification as read:', error);
  }
};

  const loadTopPerformers = async () => {
    try {
      // Load top performers for IAT-1
      const iat1Query = query(
        collection(db, 'iaTest1'),
        orderBy('percentage', 'desc'),
        limit(5)
      );
      const iat1Snapshot = await getDocs(iat1Query);
      const iat1Data = iat1Snapshot.docs.map(doc => ({
        rollNumber: doc.id,
        ...doc.data()
      }));
      setTopPerformersIAT1(iat1Data);

      // Load top performers for IAT-2
      const iat2Query = query(
        collection(db, 'iaTest2'),
        orderBy('percentage', 'desc'),
        limit(5)
      );
      const iat2Snapshot = await getDocs(iat2Query);
      const iat2Data = iat2Snapshot.docs.map(doc => ({
        rollNumber: doc.id,
        ...doc.data()
      }));
      setTopPerformersIAT2(iat2Data);
    } catch (error) {
      console.error('Error loading top performers:', error);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('studentSession');
    navigate('/');
  };

  // ✅ SECURITY FIX: Show loading while authentication is being checked
  if (loading || !rollNumber) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-600">Student not found</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 text-blue-600 hover:underline"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  // Full name with father's name in middle
  const fullName = `${student.firstName || ''} ${student.fatherName || ''} ${student.surname || ''}`.trim();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
               <img 
                src="/ybit-logo.png" 
                alt="YBIT Logo" 
                className="w-12 h-12 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Student Dashboard</h1>
                
                <p className="text-sm text-gray-600">YBIT - First Year Engineering</p>
               
              </div>
            </div>
            <div className="flex items-center space-x-4">
                  <p className="text-base text-red-700 font-bold italic ">Welcome {student.firstName || ''} {student.surname || ''}</p>
              {!isMentorView && (
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <Bell className="w-6 h-6 text-gray-700" />
                  {notifications.length > 0 && (
                    <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {notifications.length}
                    </span>
                  )}
                </button>
                
                {showNotifications && (
  <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto">
    <div className="p-4 border-b flex justify-between items-center">
      <h3 className="font-semibold text-gray-900">Notifications</h3>
      {notifications.length > 0 && (
        <button
          onClick={() => setShowNotifications(false)}
          className="text-sm text-blue-600 hover:underline"
        >
          Close
        </button>
      )}
    </div>
    {notifications.length === 0 ? (
      <div className="p-4 text-center text-gray-500">
        <Bell className="w-12 h-12 mx-auto mb-2 text-gray-300" />
        <p>No new notifications</p>
      </div>
    ) : (
      <div>
        {notifications.map(notif => (
          <div 
            key={notif.id} 
            className="p-4 border-b hover:bg-gray-50 cursor-pointer"
            onClick={() => handleMarkNotificationAsRead(notif.id)}
          >
            <div className="flex items-start space-x-3">
              <span className="text-3xl flex-shrink-0">{notif.icon}</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 text-sm">{notif.title}</p>
                <p className="text-sm text-gray-700 mt-1 break-words">{notif.message}</p>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-gray-500">
                    From: {notif.fromName}
                  </p>
                  <p className="text-xs text-gray-400">
                    {notif.createdAt?.toDate ? new Date(notif.createdAt.toDate()).toLocaleString() : 'Just now'}
                  </p>
                </div>
                {notif.actionUrl && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(notif.actionUrl);
                      setShowNotifications(false);
                    }}
                    className="mt-3 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                  >
                    {notif.actionText || 'View Details'}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
)}
              </div>
              )}
              {isMentorView ? (
                <button
                  onClick={() => window.close()}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <X className="w-4 h-4" />
                  <span>Close</span>
                </button>
              ) : (
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Top Section: ID Card + Leaderboard */}
        <div className="grid lg:grid-cols-3 gap-6 mb-4">
          {/* Mini ID Card - Left Side */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden h-full flex flex-col">
              <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-4">
                <h2 className="text-white text-base font-semibold">YBIT - Student Profile</h2>
                <p className="text-blue-100 text-sm">Semester I</p>
              </div>
              
              <div className="p-6 flex-1 flex flex-col">
           {/* Photo */}
<div className="flex justify-center mb-5">
  <div className="w-40 h-40 rounded-lg border-4 border-blue-600 overflow-hidden bg-gray-100 flex items-center justify-center">
    {getStudentPhotoUrl(student) ? (
      <img 
        src={getStudentPhotoUrl(student)}
        alt="Student" 
        className="max-w-full max-h-full object-contain"
        onError={(e) => {
          console.error('Failed to load photo:', student.photoUrl);
          e.target.style.display = 'none';
        }}
      />
    ) : (
      <User className="w-20 h-20 text-gray-400" />
    )}
  </div>
</div>
                
                {/* Details */}
                <div className="space-y-3 flex-1">
                  <div>
                    <p className="text-xs text-gray-600 font-semibold mb-1">Full Name</p>
                    <p className="text-base text-gray-900 font-bold leading-tight">{fullName}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-600 font-semibold mb-1">Roll Number</p>
                      <p className="text-base text-gray-900 font-bold">{rollNumber}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 font-semibold mb-1">Date of Birth</p>
                      <p className="text-base text-gray-900 font-bold">{student.dob || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-600 font-semibold mb-1">Division</p>
                      <p className="text-base text-gray-900 font-bold">{student.division}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 font-semibold mb-1">Batch</p>
                      <p className="text-base text-gray-900 font-bold">{student.batch}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 font-semibold mb-1">Branch</p>
                    <p className="text-base text-gray-900 font-bold">{student.branch || student.branch || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 font-semibold mb-1">Phone Number</p>
                    <p className="text-base text-gray-900 font-bold">{student.mobile || student.mobileNumber || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 font-semibold mb-1">Mentor</p>
                    <p className="text-base text-gray-900 font-bold">{student.mentorName || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 font-semibold mb-1">Class Coordinator</p>
                    <p className="text-base text-gray-900 font-bold">{student.ccName || 'N/A'}</p>
                  </div>
                </div>
                
                <button
                  onClick={() => navigate(addMentorParam('/student/profile'))}
                  className="mt-4 w-full px-4 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                >
                  View Complete Profile
                </button>
                
                <button
                  onClick={() => setShowEditModal(true)}
                  className="mt-2 w-full px-4 py-2.5 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 transition-colors"
                >
                  Edit your details
                </button>
              </div>
            </div>
          </div>



          {/* Notices, Announcements and Top Performers - Right Side */}
          <div className="lg:col-span-2 flex flex-col gap-4">
           {/* Announcements Section */}
<div className="bg-white rounded-lg shadow-lg overflow-hidden">
  <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-4">
    <h2 className="text-white text-lg font-bold text-center">NOTICES AND ANNOUNCEMENTS</h2>
  </div>
  <div className="p-4 overflow-y-auto" style={{maxHeight: '280px', minHeight: '150px'}}>
    {announcements.length === 0 ? (
      <div className="text-center py-12 text-gray-500">
        <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p>No announcements at this time</p>
      </div>
    ) : (
      <div className="space-y-3">
        {announcements.map((announcement) => {
          const read = isRead(announcement);
          const isExpanded = expandedAnnouncement === announcement.id;
          
          return (
            <div
              key={announcement.id}
              className={`border-2 rounded-lg p-3 transition-all ${
                !read ? 'border-orange-400 bg-orange-50' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="space-y-2">
                {/* Badges */}
                <div className="flex items-center gap-2 flex-wrap">
                  {announcement.priority === 'urgent' && (
                    <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded-full text-xs font-semibold">
                      URGENT
                    </span>
                  )}
                  {announcement.priority === 'high' && (
                    <span className="px-2 py-0.5 bg-orange-100 text-orange-800 rounded-full text-xs font-semibold">
                      HIGH
                    </span>
                  )}
                  {!read && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                      NEW
                    </span>
                  )}
                </div>
                
                {/* Title */}
                <h4 className="font-bold text-gray-900 text-sm">{announcement.title}</h4>
                
                {/* Message */}
                <div>
                  <p 
  className={`text-gray-700 text-xs mb-2 ${expandedAnnouncement === announcement.id ? '' : 'line-clamp-2'}`}
  onClick={() => setExpandedAnnouncement(expandedAnnouncement === announcement.id ? null : announcement.id)}
>
  {announcement.message}
</p>
                </div>
                
                {/* Footer */}
                <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-200">
                  <div className="text-xs text-gray-500">
                    <span>{announcement.createdByName}</span>
                    <span className="mx-1">•</span>
                    <span>{announcement.createdAt.toLocaleDateString()}</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setExpandedAnnouncement(isExpanded ? null : announcement.id);
                        if (!read) handleMarkAsRead(announcement.id);
                      }}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700"
                    >
                      {isExpanded ? '▲ Less' : '▼ More'}
                    </button>
                    
                    {!read && (
                      <button
                        onClick={() => handleMarkAsRead(announcement.id)}
                        className="px-3 py-1 bg-orange-600 text-white rounded text-xs font-medium hover:bg-orange-700"
                      >
                        ✓ Read
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
</div>
            {/* Top Performers Section */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-yellow-500 to-orange-600 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Trophy className="w-6 h-6 text-white" />
                    <h2 className="text-white text-lg font-bold">Top Performers</h2>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setActiveLeaderboard('iat1')}
                      className={`px-4 py-1.5 rounded text-sm font-semibold transition-colors ${
                        activeLeaderboard === 'iat1'
                          ? 'bg-white text-orange-600'
                          : 'bg-orange-700 text-white hover:bg-orange-800'
                      }`}
                    >
                      IA Test 1
                    </button>
                    <button
                      onClick={() => setActiveLeaderboard('iat2')}
                      className={`px-4 py-1.5 rounded text-sm font-semibold transition-colors ${
                        activeLeaderboard === 'iat2'
                          ? 'bg-white text-orange-600'
                          : 'bg-orange-700 text-white hover:bg-orange-800'
                      }`}
                    >
                      IA Test 2
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="p-4 overflow-y-auto" style={{maxHeight: '450px', minHeight: '350px'}}>
                {activeLeaderboard === 'iat1' && (
                  <div className="space-y-3">
                    {topPerformersIAT1.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">No data available yet</p>
                    ) : (
                      topPerformersIAT1.map((performer, index) => (
                        <div
                          key={performer.rollNumber}
                          className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                            performer.rollNumber === rollNumber
                              ? 'bg-blue-50 border-blue-500 shadow-md'
                              : 'bg-gray-50 border-gray-200 hover:shadow-md'
                          }`}
                        >
                          <div className="flex items-center space-x-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                              index === 0 ? 'bg-yellow-400 text-yellow-900' :
                              index === 1 ? 'bg-gray-300 text-gray-700' :
                              index === 2 ? 'bg-orange-400 text-orange-900' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                            </div>
                            <div>
                              <p className="font-bold text-gray-900">{performer.studentName || performer.rollNumber}</p>
                              <p className="text-sm text-gray-600">{performer.rollNumber}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-green-600">{Number(performer.percentage).toFixed(2)}%</p>
                            <p className="text-sm text-gray-600">{performer.total}/105</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
                {activeLeaderboard === 'iat2' && (
                  <div className="space-y-3">
                    {topPerformersIAT2.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">No data available yet</p>
                    ) : (
                      topPerformersIAT2.map((performer, index) => (
                        <div
                          key={performer.rollNumber}
                          className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                            performer.rollNumber === rollNumber
                              ? 'bg-blue-50 border-blue-500 shadow-md'
                              : 'bg-gray-50 border-gray-200 hover:shadow-md'
                          }`}
                        >
                          <div className="flex items-center space-x-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                              index === 0 ? 'bg-yellow-400 text-yellow-900' :
                              index === 1 ? 'bg-gray-300 text-gray-700' :
                              index === 2 ? 'bg-orange-400 text-orange-900' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                            </div>
                            <div>
                              <p className="font-bold text-gray-900">{performer.studentName || performer.rollNumber}</p>
                              <p className="text-sm text-gray-600">{performer.rollNumber}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-green-600">{Number(performer.percentage).toFixed(2)}%</p>
                            <p className="text-sm text-gray-600">{performer.total}/105</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Stats Cards - All in One Row with Fixed Height */}
        <div className="grid md:grid-cols-3 gap-6 mb-6">
          {/* Attendance Card */}
          <div className="bg-white rounded-lg shadow-md p-6 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Attendance</h3>
              <TrendingUp className={`w-6 h-6 ${attendance?.overallPercentage >= 75 ? 'text-green-500' : 'text-red-500'}`} />
            </div>
            {attendance ? (
              <>
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {Number(attendance.overallPercentage).toFixed(2)}%
                </div>
                {attendance.overallPercentage < 75 && (
                  <div className="bg-red-50 border border-red-200 rounded p-2 mb-3">
                    <p className="text-red-700 text-sm font-medium">⚠️ Below Required 75%</p>
                  </div>
                )}
                <div className="space-y-2 text-sm text-gray-600 mb-4 flex-1">
                  <p>Theory: {Number(attendance.theoryPercentage).toFixed(2)}%</p>
                  <p>Practical: {Number(attendance.practicalPercentage).toFixed(2)}%</p>
                </div>
                <button
                  onClick={() => navigate(addMentorParam('/student/attendance-report'))}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mt-auto"
                >
                  <Eye className="w-4 h-4" />
                  <span>View Report</span>
                </button>
              </>
            ) : (
              <p className="text-gray-500 flex-1">No data available</p>
            )}
          </div>

          {/* IA Test 1 Card */}
          <div className="bg-white rounded-lg shadow-md p-6 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">IA Test 1</h3>
              <FileText className="w-6 h-6 text-blue-500" />
            </div>
            {iaTest1 ? (
              <>
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {Number(iaTest1.percentage).toFixed(2)}%
                </div>
                <div className="space-y-2 text-sm text-gray-600 mb-4 flex-1">
                  <p>Total: {iaTest1.total}/105</p>
                  <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                    <div>AM: {iaTest1.subjects?.amI}</div>
                    <div>AP: {iaTest1.subjects?.ap}</div>
                    <div>AC: {iaTest1.subjects?.ac}</div>
                    <div>EM: {iaTest1.subjects?.em}</div>
                    <div>BEE: {iaTest1.subjects?.bee}</div>
                    <div>PCE: {iaTest1.subjects?.pce}</div>
                  </div>
                </div>
                <button
                  onClick={() => navigate(addMentorParam('/student/ia-test1-report'))}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mt-auto"
                >
                  <Eye className="w-4 h-4" />
                  <span>View Report</span>
                </button>
              </>
            ) : (
              <p className="text-gray-500 flex-1">Results pending</p>
            )}
          </div>

          {/* IA Test 2 Card */}
          <div className="bg-white rounded-lg shadow-md p-6 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">IA Test 2</h3>
              <FileText className="w-6 h-6 text-green-500" />
            </div>
            {iaTest2 ? (
              <>
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {Number(iaTest2.percentage).toFixed(2)}%
                </div>
                <div className="space-y-2 text-sm text-gray-600 mb-4 flex-1">
                  <p>Total: {iaTest2.total}/105</p>
                  <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                    <div>AM: {iaTest2.subjects?.amI}</div>
                    <div>AP: {iaTest2.subjects?.ap}</div>
                    <div>AC: {iaTest2.subjects?.ac}</div>
                    <div>EM: {iaTest2.subjects?.em}</div>
                    <div>BEE: {iaTest2.subjects?.bee}</div>
                    <div>PCE: {iaTest2.subjects?.pce}</div>
                  </div>
                </div>
                <button
                  onClick={() => navigate(addMentorParam('/student/ia-test2-report'))}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mt-auto"
                >
                  <Eye className="w-4 h-4" />
                  <span>View Report</span>
                </button>
              </>
            ) : (
              <p className="text-gray-500 flex-1">Results pending</p>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Quick Actions</h3>
          <div className="grid md:grid-cols-4 gap-4">
            <button
              onClick={() => navigate(addMentorParam('/student/achievements'))}
              className="flex items-center justify-center space-x-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-md"
            >
              <Award className="w-6 h-6" />
              <span className="font-semibold">Performance/Achievements</span>
            </button>
            <button
              onClick={() => navigate(addMentorParam('/student/apply-leave'))}
              className="flex items-center justify-center space-x-3 bg-gradient-to-r from-green-600 to-green-700 text-white p-4 rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-md"
            >
              <Calendar className="w-6 h-6" />
              <span className="font-semibold">Apply for Leave</span>
            </button>
            <button
              onClick={() => navigate(addMentorParam('/student/psychometric-test'))}
              className="flex items-center justify-center space-x-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white p-4 rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-md"
            >
              <Brain className="w-6 h-6" />
              <span className="font-semibold">Take Career Test</span>
            </button>
            <button
              onClick={() => navigate(addMentorParam('/student/end-semester-report'))}
              className="flex items-center justify-center space-x-3 bg-gradient-to-r from-slate-700 to-slate-800 text-white p-4 rounded-lg hover:from-slate-800 hover:to-slate-900 transition-all shadow-md"
            >
              <FileText className="w-6 h-6" />
              <span className="font-semibold">End Semester Report</span>
            </button>
          </div>
        </div>

        {/* Achievements Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">My Achievements</h3>
            <button
              onClick={() => navigate(addMentorParam('/student/achievements'))}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              + Add Achievement
            </button>
          </div>

          {achievements.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Award className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No achievements added yet</p>
              <p className="text-sm mt-2">Click "Add Achievement" to get started!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {achievements.map(achievement => (
                <div key={achievement.id} className="border-2 border-gray-200 rounded-lg p-6 hover:border-blue-300 hover:shadow-lg transition-all bg-white">
                  {/* Achievement Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <Award className="w-6 h-6 text-blue-600 flex-shrink-0" />
                        <h4 className="font-bold text-xl text-gray-900">{achievement.title}</h4>
                      </div>
                      <p className="text-gray-600 text-base leading-relaxed ml-9">{achievement.description}</p>
                    </div>
                    
                    {/* Verified Badge */}
                    <span className="flex items-center space-x-2 px-4 py-2 rounded-full bg-green-100 text-green-800 font-semibold text-sm flex-shrink-0 ml-4">
                      <CheckCircle className="w-4 h-4" />
                      <span>Verified</span>
                    </span>
                  </div>

                  {/* Achievement Details */}
                  <div className="flex flex-wrap items-center gap-3 mb-4 ml-9">
                    {/* Category */}
                    <span className="px-3 py-1.5 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                      {achievement.category?.replace('-', ' ').toUpperCase()}
                    </span>
                    
                    {/* Subcategory */}
                    {achievement.subcategory && (
                      <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                        {achievement.subcategory}
                      </span>
                    )}
                    
                    {/* Date */}
                    {achievement.date && (
                      <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
                        📅 {new Date(achievement.date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </span>
                    )}
                  </div>

                  {/* View Document Button */}
                  {achievement.documentUrl && (
                    <div className="ml-9">
                      <button
                        onClick={() => openDocument(achievement.documentUrl)}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                      >
                        <Eye className="w-4 h-4" />
                        <span>View Certificate</span>
                      </button>
                    </div>
                  )}

                  {/* Verified By Footer */}
                  {achievement.verifiedBy && (
                    <div className="mt-4 pt-4 border-t border-gray-200 ml-9">
                      <p className="text-xs text-gray-500">
                        Verified by <span className="font-semibold text-gray-700">{achievement.verifiedBy}</span>
                        {formatDisplayDate(achievement.verificationDate) && (
                          <span> on {formatDisplayDate(achievement.verificationDate)}</span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit Details Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center z-10">
              <h2 className="text-2xl font-bold text-gray-800">Edit Your Details</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>
            <div className="p-6">
              <EditDetailsForm 
                rollNumber={rollNumber} 
                onClose={() => {
                  setShowEditModal(false);
                  loadStudentData(); // Reload data after edit
                }} 
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentDashboard;
